import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Award, ShieldCheck, MapPin, Phone, Check } from 'lucide-react';
import { api } from '../lib/api';
import { useContent } from '../lib/content';
import { SEO } from '../components/SEO';
import { VehicleCard } from '../components/VehicleCard';
import { Button } from '../components/ui/button';
import { Skeleton } from '../components/ui/skeleton';

const FEATURE_ICONS = [Award, ShieldCheck, MapPin];

export default function Home() {
  const content = useContent();
  const [featured, setFeatured] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api
      .get('/vehicles', { params: { featured_only: true, limit: 4 } })
      .then((res) => {
        let list = res.data.vehicles;
        if (list.length === 0) {
          return api.get('/vehicles', { params: { limit: 4 } }).then((r2) => setFeatured(r2.data.vehicles));
        }
        setFeatured(list);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const { hero, why_choose, cta } = content;

  return (
    <>
      <SEO
        title="Quality Trucks & Trailers Australia"
        description="Higgins Truck Sales - quality trucks and trailers available across Australia. Browse our range of prime movers, rigids, curtainsiders and trailers for sale."
        path="/"
      />

      {/* Hero */}
      <section className="relative min-h-[78vh] flex items-center overflow-hidden hero-noise" data-testid="home-hero">
        <img
          src={hero.image_url}
          alt="Truck on the highway"
          className="absolute inset-y-0 left-0 h-full w-full lg:w-[120%] max-w-none object-cover object-[center_62%]"
          fetchpriority="high"
        />
        <div className="absolute inset-0 hero-overlay" />
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 w-full">
          <div className="lg:w-1/2">
            <h1 className="font-heading uppercase text-white text-3xl sm:text-4xl lg:text-6xl leading-[0.95] tracking-wider animate-fade-up">
              {hero.headline_line1}
              <br />
              {hero.headline_line2}
            </h1>
            <p className="mt-5 max-w-xl text-white/90 text-base sm:text-lg font-medium animate-fade-up-delay-1">
              {hero.subheading}
            </p>
            <ul className="mt-5 grid grid-cols-2 gap-x-6 gap-y-2.5 max-w-md animate-fade-up-delay-1">
              {(hero.points || []).map((point) => (
                <li key={point} className="flex items-center gap-2.5 text-white/85 text-sm sm:text-base">
                  <span className="h-5 w-5 rounded-full bg-[#E4002B] flex items-center justify-center shrink-0">
                    <Check className="h-3 w-3 text-white" strokeWidth={3} />
                  </span>
                  {point}
                </li>
              ))}
            </ul>
            <div className="mt-8 flex flex-wrap gap-4 animate-fade-up-delay-2">
              <Button
                asChild
                data-testid="hero-view-trucks-button"
                className="rounded-full bg-[#E4002B] text-white hover:bg-[#E4002B]/90 h-12 px-8 text-sm font-bold tracking-wider transition-colors duration-150"
              >
                <Link to="/trucks">{hero.primary_button}</Link>
              </Button>
              <Button
                asChild
                variant="outline"
                data-testid="hero-contact-us-button"
                className="rounded-full border border-white/70 bg-transparent text-white hover:bg-white/10 hover:text-white h-12 px-8 text-sm font-bold tracking-wider transition-colors duration-150"
              >
                <Link to="/contact">{hero.secondary_button}</Link>
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Why choose */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16" data-testid="home-features">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {(why_choose || []).map((f, idx) => {
            const Icon = FEATURE_ICONS[idx % FEATURE_ICONS.length];
            return (
              <div
                key={f.title}
                className="flex items-start gap-4 bg-white border border-[#D9D9D6] rounded-xl p-6 hover:shadow-[0_10px_30px_rgba(36,19,95,0.08)] transition-shadow duration-200"
              >
                <div className="shrink-0 h-12 w-12 rounded-full border-2 border-[#E4002B] flex items-center justify-center">
                  <Icon className="h-5 w-5 text-[#E4002B]" />
                </div>
                <div>
                  <h3 className="font-heading text-lg tracking-wider text-[#24135F]">{f.title}</h3>
                  <p className="text-sm text-[#53565A] mt-1 leading-relaxed">{f.text}</p>
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* Featured trucks */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-16" data-testid="home-featured-trucks">
        <div className="flex items-end justify-between mb-6">
          <h2 className="font-heading text-3xl sm:text-4xl tracking-wider text-[#24135F]">FEATURED TRUCKS</h2>
          <Link
            to="/trucks"
            data-testid="view-all-trucks-link"
            className="text-[#E4002B] text-sm font-bold tracking-wider hover:text-[#E4002B]/80 transition-colors duration-150 flex items-center gap-1 shrink-0"
          >
            VIEW ALL TRUCKS <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
        {loading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="space-y-3">
                <Skeleton className="aspect-[4/3] rounded-xl" />
                <Skeleton className="h-5 w-3/4" />
                <Skeleton className="h-6 w-1/2" />
              </div>
            ))}
          </div>
        ) : featured.length === 0 ? (
          <p className="text-[#53565A] text-sm py-8">No vehicles listed yet. Check back soon.</p>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {featured.map((v) => (
              <VehicleCard key={v.id} vehicle={v} />
            ))}
          </div>
        )}
      </section>

      {/* Contact CTA */}
      <section className="bg-[#24135F] relative overflow-hidden" data-testid="home-contact-cta">
        <div className="absolute left-0 top-0 bottom-0 w-1.5 bg-[#E4002B]" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14 sm:py-16 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6">
          <div>
            <h2 className="font-heading text-3xl sm:text-4xl tracking-wider text-white">{cta.heading}</h2>
            <p className="text-white/60 mt-2 text-sm sm:text-base">{cta.subtext}</p>
          </div>
          <div className="flex items-center gap-4 shrink-0">
            <Button
              asChild
              data-testid="cta-contact-button"
              className="rounded-full bg-[#E4002B] text-white hover:bg-[#E4002B]/90 h-12 px-8 text-sm font-bold tracking-wider transition-colors duration-150"
            >
              <Link to="/contact">
                <Phone className="h-4 w-4 mr-2" /> {cta.button}
              </Link>
            </Button>
          </div>
        </div>
      </section>
    </>
  );
}
